import { Link } from '@remix-run/react';
import { useState } from 'react';
import type { Image } from 'types/capi/shared';

import ChevronIcon from '~/assets/icons/Chevron';
import VectorIcon from '~/assets/icons/Vector';
import WarningIcon from '~/assets/icons/Warning';
import useUser from '~/hooks/useUser';
import { isAppleMobileDevice, isInAppBrowser } from '~/utils/browser';

import { Dialog, DialogDismiss } from '../../Dialog';
import DownloadAppFooter from './DownloadAppFooter';
import ForgotPasswordScreen from './ForgotPasswordScreen';
import InitialScreen from './InitialScreen';
import OrSeparator from './OrSeperator';
import OtherLoginOptionsMessage from './OtherLoginOptionsMessage';
import SignInScreen from './SignInScreen';
import SignUpCompleteScreen from './SignUpCompleteScreen';
import SignUpScreen from './SignUpScreen';

export type LoginModalType =
  | 'notifications'
  | 'profile'
  | 'purchase'
  | 'raffle'
  | 'pdpGate';

export type PostAuthOptions = {
  action: 'redirect' | 'like' | 'restock';
  legacyProductId?: string;
  onSuccess?: () => void;
  productName?: string;
  redirectUrl?: string;
  sizes?: string[];
};

export type ScreenName =
  | 'init'
  | 'signIn'
  | 'signUp'
  | 'signUpComplete'
  | 'forgotPassword';

export type ScreenProps = {
  image?: Image;
  modalType: LoginModalType;
  name: ScreenName;
  onDismiss: (options?: { loginSuccessful?: boolean }) => void;
  onError: (err: string | null) => void;
  postAuthOptions?: PostAuthOptions;
  setScreen: (name: ScreenName) => void;
};

export function Screen(props: ScreenProps) {
  const [email, setEmail] = useState('');

  switch (props.name) {
    case 'init':
      return <InitialScreen {...props} />;
    case 'signIn':
      return <SignInScreen {...props} />;
    case 'signUp':
      return <SignUpScreen {...props} setEmail={setEmail} />;
    case 'signUpComplete':
      return <SignUpCompleteScreen {...props} email={email} />;
    case 'forgotPassword':
      return <ForgotPasswordScreen />;
  }
}

type Props = {
  hideAppDownloadPrompt?: boolean;
  image?: Image;
  modalType: LoginModalType;
  onDismiss: (options?: { loginSuccessful?: boolean }) => void;
  postAuthOptions?: PostAuthOptions;
  screenName?: ScreenName;
};

export function Login({
  onDismiss,
  screenName = 'init',
  postAuthOptions,
  image,
  modalType,
  hideAppDownloadPrompt = false
}: Props) {
  const [screen, setScreen] = useState<ScreenName>(screenName);
  const [error, setError] = useState<string | null>(null);
  const user = useUser();

  function updateScreenView(name: ScreenName) {
    setError(null);
    setScreen(name);
  }

  function onBackNavigation() {
    return screen === 'forgotPassword'
      ? updateScreenView('signIn')
      : updateScreenView('init');
  }

  if (user.isLoggedIn) return null;

  const isWhiteBackground = screen === 'init';
  const modalBackground = isWhiteBackground
    ? 'bg-white'
    : 'border border-white bg-black';

  const showAppDownload =
    isAppleMobileDevice(navigator.userAgent) &&
    !hideAppDownloadPrompt &&
    screen === 'init' &&
    !['purchase', 'profile'].includes(modalType);

  const isInApp = isInAppBrowser(navigator.userAgent);

  return (
    <Dialog
      isOpen={true}
      onDismiss={onDismiss}
      className={`z-50 my-4 flex w-[500px] max-w-[90%] flex-col rounded-lg p-4 ${modalBackground}`}
    >
      <div data-cy="login-modal">
        {screen !== 'init' && (
          <nav className="flex flex-row items-center justify-between">
            <button className="rotate-90" onClick={onBackNavigation}>
              <ChevronIcon stroke="white" width="14" height="14" />
            </button>
            {modalType === 'profile' ? (
              <Link to="/">
                <VectorIcon fill="white" width="10" height="10" />
              </Link>
            ) : (
              <DialogDismiss className="text-black">
                <VectorIcon fill="white" width="10" height="10" />
              </DialogDismiss>
            )}
          </nav>
        )}
        <Screen
          name={screen}
          onDismiss={onDismiss}
          setScreen={updateScreenView}
          postAuthOptions={postAuthOptions}
          onError={setError}
          image={image}
          modalType={modalType}
        />
        {error && (
          <ErrorMessage error={error} isWhiteBackground={isWhiteBackground} />
        )}
        {showAppDownload && <DownloadAppFooter modalType={modalType} />}
        {isInApp && (
          <>
            <OrSeparator />
            <OtherLoginOptionsMessage isOnBlack={screen === 'signIn'} />
          </>
        )}
      </div>
    </Dialog>
  );
}

function ErrorMessage({
  error,
  isWhiteBackground
}: {
  error: string;
  isWhiteBackground: boolean;
}) {
  return (
    <p
      className={`mt-4 flex items-center gap-2 rounded p-2 text-sm font-normal ${
        isWhiteBackground
          ? 'bg-red-alert/10 text-red-alert'
          : 'bg-red-alert/75 text-white'
      }`}
    >
      <span className="w-6">
        <WarningIcon fill={isWhiteBackground ? '#D71225' : 'white'} />
      </span>
      {error}
    </p>
  );
}
